import { api } from "@clarent/backend/convex/_generated/api";
import type { Id } from "@clarent/backend/convex/_generated/dataModel";
import { stepHours, type ISODate, type JobStatus } from "@clarent/scheduling";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { useState } from "react";
import { JobDrawer } from "../components/JobDrawer";
import { JobModal } from "../components/JobModal";
import { matchesSearch, PageHeader } from "../components/PageHeader";
import { RiskBadge } from "../components/RiskBadge";
import { SummaryStrip } from "../components/SummaryStrip";
import { errorMessage } from "../lib/convexError";
import { formatDateLong, formatDay, formatHours, jobTitle, todayContext } from "../lib/format";
import { useMachines } from "../lib/machines";
import { PROCESS_META, processKind } from "../lib/process";
import { isLate, STATUS_META, STATUS_ORDER } from "../lib/risk";
import { useToday } from "../lib/today";

export type JobRow = FunctionReturnType<typeof api.jobs.listWithSchedule>[number];

/**
 * The board, with the overlay its route asks for: `/jobs/$jobId` opens the job drawer,
 * `/jobs/new` and `/jobs/$jobId/edit` open the job modal (decision D38).
 */
export function BoardPage({ overlay }: { overlay?: { kind: "drawer" | "edit"; jobId: Id<"jobs"> } | { kind: "new" } }) {
  const today = useToday();
  const navigate = useNavigate();
  const jobs = useQuery(api.jobs.listWithSchedule, { today });
  const committed = useQuery(api.jobs.committedWindows, { today });
  const machines = useMachines();
  const setStatus = useMutation(api.jobs.setStatus);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState<JobRow | null>(null);
  // The drag origin gap and the drop zone take the height of the card being dragged.
  const [dragHeight, setDragHeight] = useState(118);

  // Space picks a card up for keyboard dragging, so Enter is free to open it.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Enter"] } }),
  );

  const onDragStart = (e: DragStartEvent) => {
    setDragging(jobs?.find((j) => j._id === e.active.id) ?? null);
    setDragHeight(e.active.rect.current.initial?.height ?? 118);
  };
  const onDragEnd = async (e: DragEndEvent) => {
    setDragging(null);
    const jobId = e.active.id as Id<"jobs">;
    const status = e.over?.id as JobStatus | undefined;
    const from = e.active.data.current?.status as JobStatus | undefined;
    if (!status || status === from) return;
    try {
      setError(null);
      await setStatus({ jobId, status });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const openId = overlay && overlay.kind !== "new" ? overlay.jobId : undefined;
  const visible = jobs?.filter((j) => matchesSearch(search, j.reference, j.customerName, j.part));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Board" context={todayContext(today)} view="board" search={search} onSearch={setSearch} />
      {jobs && committed && machines && <SummaryStrip jobs={jobs} committed={committed} weeklyHours={machines.weeklyHours} today={today} />}
      {error && <p className="notice-error">{error}</p>}
      {visible === undefined ? (
        <p className="text-sm text-muted">Loading board…</p>
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
          <div className="grid auto-cols-[minmax(214px,1fr)] grid-flow-col items-start gap-3 overflow-x-auto pb-2">
            {STATUS_ORDER.map((status) => (
              <Column
                key={status}
                status={status}
                today={today}
                openId={openId}
                dragging={dragging}
                dragHeight={dragHeight}
                onOpen={(jobId) => navigate({ to: "/jobs/$jobId", params: { jobId } })}
                jobs={visible.filter((j) => j.status === status).sort((a, b) => a.schedule.latestStartDate.localeCompare(b.schedule.latestStartDate))}
              />
            ))}
          </div>
          <DragOverlay dropAnimation={null}>{dragging && <JobCard job={dragging} today={today} lifted />}</DragOverlay>
        </DndContext>
      )}
      {search && visible?.length === 0 && <p className="text-sm text-muted">No job or customer matches "{search}".</p>}

      {overlay?.kind === "drawer" && (
        <JobDrawer jobId={overlay.jobId} row={jobs?.find((j) => j._id === overlay.jobId)} onClose={() => navigate({ to: "/board" })} />
      )}
      {overlay?.kind === "new" && <JobModal onClose={() => navigate({ to: "/board" })} onSaved={() => navigate({ to: "/board" })} />}
      {overlay?.kind === "edit" && (
        <JobModal
          jobId={overlay.jobId}
          onClose={() => navigate({ to: "/jobs/$jobId", params: { jobId: overlay.jobId } })}
          onSaved={() => navigate({ to: "/jobs/$jobId", params: { jobId: overlay.jobId } })}
        />
      )}
    </div>
  );
}

function Column({
  status,
  jobs,
  today,
  openId,
  dragging,
  dragHeight,
  onOpen,
}: {
  status: JobStatus;
  jobs: JobRow[];
  today: ISODate;
  openId?: string;
  dragging: JobRow | null;
  dragHeight: number;
  onOpen: (jobId: Id<"jobs">) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const showDrop = isOver && dragging !== null && dragging.status !== status;
  // Design: Scheduled also shows the machining hours waiting.
  const waiting = status === "scheduled" ? jobs.reduce((sum, j) => sum + j.steps.reduce((h, s) => h + (s.type === "machining" ? stepHours(s) : 0), 0), 0) : 0;

  return (
    <section ref={setNodeRef} className="flex min-h-[60vh] flex-col gap-2.5 rounded-tile bg-column p-2.5" aria-label={STATUS_META[status].label}>
      <header className="flex items-center gap-2 px-1 pt-0.5 pb-1">
        <span className={`size-2.5 shrink-0 rounded-[3px] ${STATUS_META[status].marker}`} />
        <h2 className="text-[13px] leading-4 font-bold">{STATUS_META[status].label}</h2>
        <span className="font-mono text-[11px] leading-[14px] text-muted">{jobs.length}</span>
        {waiting > 0 && <span className="ml-auto font-mono text-[11px] leading-[14px] text-subtle">{formatHours(waiting)}</span>}
      </header>
      {jobs.map((job) => (
        <DraggableCard key={job._id} job={job} today={today} selected={job._id === openId} gapHeight={dragHeight} onOpen={() => onOpen(job._id)} />
      ))}
      {showDrop && (
        <div
          className="flex shrink-0 items-start justify-center rounded-md border-2 border-dashed border-brand bg-brand-tint pt-3.5 text-[13px] leading-4 font-bold text-brand-dark"
          style={{ height: dragHeight }}
        >
          {STATUS_META[status].drop}
        </div>
      )}
    </section>
  );
}

function DraggableCard({ job, today, selected, gapHeight, onOpen }: { job: JobRow; today: ISODate; selected: boolean; gapHeight: number; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: job._id, data: { status: job.status } });
  if (isDragging) {
    // Drag origin: a dashed gap the size of the card that left.
    return <div ref={setNodeRef} className="shrink-0 rounded-md border-2 border-dashed border-line bg-white/40" style={{ height: gapHeight }} />;
  }
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label={`${job.reference}, ${jobTitle(job)}${job.part ? ` for ${job.customerName}` : ""}. Enter opens it, Space picks it up.`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
        else listeners?.onKeyDown?.(e);
      }}
      className="cursor-grab rounded-md outline-none select-none focus-visible:ring-4 focus-visible:ring-brand-tint active:cursor-grabbing"
    >
      <JobCard job={job} today={today} selected={selected} />
    </div>
  );
}

/** Card states from design system v2.1 "Board states": default, selected (the open job) and lifted (being dragged). */
export function JobCard({ job, today, selected = false, lifted = false }: { job: JobRow; today: ISODate; selected?: boolean; lifted?: boolean }) {
  const risk = job.schedule.riskLevel;
  const complete = job.status === "complete";
  const processes = complete ? [] : job.steps.filter((s) => s.type === "post_process");
  const frame = selected
    ? "border-2 border-brand p-[11px] ring-4 ring-brand-tint"
    : lifted
      ? "border border-line p-3 shadow-drag rotate-3"
      : "border border-line p-3 shadow-card";
  return (
    <article className={`flex flex-col gap-2 rounded-md bg-bg ${frame}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] leading-3 text-muted">{job.reference}</span>
        <RiskBadge risk={risk} done={complete} />
      </div>
      <div className="flex flex-col gap-px">
        <h3 className="text-sm leading-[18px] font-bold">{jobTitle(job)}</h3>
        {job.part && <span className="text-xs leading-4 text-muted">{job.customerName}</span>}
      </div>
      {processes.length > 0 && (
        <div className="flex flex-wrap gap-[5px]">
          {processes.map((step, i) => (
            <span key={i} className={`tag ${PROCESS_META[processKind(step.name)].tag}`}>
              {step.name} +{step.leadDays}d
            </span>
          ))}
        </div>
      )}
      <div className={`font-mono text-[11px] leading-[14px] ${!complete && isLate(risk) ? "text-risk-text" : "text-ink"}`}>{footerDate(job, today)}</div>
    </article>
  );
}

/**
 * Design system card footer: "Start by" while quoted or scheduled, "Due" once running,
 * "Back" at post-process (the planned return of the current outside step), "Shipped" once
 * complete. Jobs completed before shipped dates were recorded show their due date (D43).
 */
function footerDate(job: JobRow, today: ISODate): string {
  switch (job.status) {
    case "quoted":
    case "scheduled":
      return `Start by ${formatDateLong(job.schedule.latestStartDate)}`;
    case "at_post_process": {
      const outside = job.schedule.timeline.filter((s) => s.kind === "post_process");
      const current = [...outside].reverse().find((s) => s.start <= today) ?? outside[0];
      if (current) return `Back ${formatDateLong(current.transitBack.end)}`;
      return `Due ${formatDateLong(job.dueDate)}`;
    }
    case "complete":
      return job.completedAt !== undefined ? `Shipped ${formatDay(new Date(job.completedAt))}` : `Due ${formatDateLong(job.dueDate)}`;
    case "in_progress":
      return `Due ${formatDateLong(job.dueDate)}`;
  }
}
